import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { FiSearch } from 'react-icons/fi';
import NativeDateField from './common/NativeDateField';
import './InventoryLedgerModal.css';

const LABEL_CLOSE_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M17.7072 0.292849C17.3167 -0.0976164 16.6836 -0.0976164 16.2932 0.292849L8.99999 7.58604L1.70683 0.292849C1.31637 -0.0976164 0.683306 -0.0976164 0.29285 0.292849C-0.0976166 0.683305 -0.0976166 1.31637 0.29285 1.70683L7.58602 9.00001L0.29287 16.2931C-0.0975966 16.6836 -0.0975966 17.3166 0.29287 17.7072C0.683326 18.0976 1.31639 18.0976 1.70685 17.7072L8.99999 10.414L16.2932 17.7072C16.6836 18.0976 17.3167 18.0976 17.7072 17.7072C18.0976 17.3166 18.0976 16.6836 17.7072 16.2932L10.414 9.00001L17.7072 1.70683C18.0976 1.31637 18.0976 0.683305 17.7072 0.292849Z" fill="#424143" />
  </svg>
);

const labelClass = 'block text-sm font-normal text-[#424143] mb-1';
const readOnlyClass =
  'w-full h-10 px-3 border border-gray-300 rounded-md text-sm bg-gray-50 text-[#2C2C2C] cursor-not-allowed';
const inputClass =
  'w-full h-10 px-3 border border-gray-300 rounded-md text-sm bg-white text-[#2C2C2C] focus:outline-none focus:ring-1 focus:ring-blue-500';

const INVENTORY_LEDGER_FORM_ID = 'inventory-ledger-modal-form';

const mdyToISO = (mdy) => {
  const slashMatch = String(mdy || '').match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, month, day, year] = slashMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  const dashMatch = String(mdy || '').match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dashMatch) {
    const [, month, day, year] = dashMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(mdy)) return mdy;
  return '';
};

const isoToMDY = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${Number(m)}/${Number(d)}/${y}`;
};

const todayMDY = () => {
  const d = new Date();
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

const getSavedByPayload = (accounts) => {
  const account = accounts?.[0];
  const savedByName =
    (typeof window !== 'undefined' && window.__currentUserDisplayName) ||
    account?.name ||
    '';
  const savedByEmail = account?.username || '';
  return {
    savedByName: String(savedByName).trim(),
    savedByEmail: String(savedByEmail).trim(),
  };
};

const getProjectIdFromRecord = (project) =>
  String(project?.Project__r?.Project_System_Wide_ID__c || '').trim();

const getProjectNameFromRecord = (project) =>
  String(project?.Project__r?.Name || '').trim();

const formatProjectOptionLabel = (project) => {
  const id = getProjectIdFromRecord(project);
  const name = getProjectNameFromRecord(project);
  if (id && name) return `${id} — ${name}`;
  return id || name || 'Unknown project';
};

const PROJECT_SEARCH_RESULT_LIMIT = 20;

const createDefaultItemEntry = () => ({
  takenToProject: 0,
  returnedFromProject: 0,
  dailyNotes: '',
});

function ReadOnlyField({ label, value, className = '' }) {
  return (
    <div className={className}>
      <label className={labelClass}>{label}</label>
      <input type="text" readOnly value={value || '—'} className={readOnlyClass} />
    </div>
  );
}

function InventoryLedgerModal({ isOpen, onClose, editingItem, accounts, getAuthConfig, onSaved }) {
  const isEdit = Boolean(editingItem);
  const projectSearchRef = useRef(null);
  const activityDropdownRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const [workDateIso, setWorkDateIso] = useState('');
  const [projectNo, setProjectNo] = useState('');
  const [projectName, setProjectName] = useState('');
  const [branchLocation, setBranchLocation] = useState('');
  const [division, setDivision] = useState('');
  const [activity, setActivity] = useState('');
  const [activities, setActivities] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectError, setProjectError] = useState(null);

  const [allProjects, setAllProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [isActivityDropdownOpen, setIsActivityDropdownOpen] = useState(false);

  const [stockOptions, setStockOptions] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockError, setStockError] = useState(null);
  const [existingStockItemIds, setExistingStockItemIds] = useState([]);
  const [existingItemsLoading, setExistingItemsLoading] = useState(false);
  const [selectedStockItemIds, setSelectedStockItemIds] = useState([]);
  const [isInventoryPickerOpen, setIsInventoryPickerOpen] = useState(false);
  const [itemEntryValues, setItemEntryValues] = useState({});

  const [takenToProject, setTakenToProject] = useState(0);
  const [returnedFromProject, setReturnedFromProject] = useState(0);
  const [dailyNotes, setDailyNotes] = useState('');

  const selectedStockItems = useMemo(
    () =>
      stockOptions.filter((opt) => selectedStockItemIds.includes(String(opt.id))),
    [stockOptions, selectedStockItemIds]
  );

  const availableStockOptions = useMemo(
    () =>
      stockOptions.filter((item) => !existingStockItemIds.includes(String(item.id))),
    [stockOptions, existingStockItemIds]
  );

  const resetAddForm = useCallback(() => {
    const todayIso = mdyToISO(todayMDY());
    setWorkDateIso(todayIso);
    setProjectNo('');
    setProjectName('');
    setBranchLocation('');
    setDivision('');
    setActivity('');
    setActivities([]);
    setProjectError(null);
    setStockOptions([]);
    setStockError(null);
    setExistingStockItemIds([]);
    setSelectedStockItemIds([]);
    setIsInventoryPickerOpen(false);
    setItemEntryValues({});
    setTakenToProject(0);
    setReturnedFromProject(0);
    setDailyNotes('');
    setFormError(null);
    setProjectSearchQuery('');
    setIsProjectDropdownOpen(false);
    setIsActivityDropdownOpen(false);
  }, []);

  const loadEditForm = useCallback(() => {
    if (!editingItem) return;
    setWorkDateIso(editingItem.dateIso || mdyToISO(editingItem.date));
    setProjectNo(editingItem.projectNo || '');
    setProjectName(editingItem.projectName || '');
    setBranchLocation(editingItem.branchLocation || '');
    setDivision(editingItem.division || '');
    setActivity(editingItem.activity || '');
    setActivities([]);
    setTakenToProject(Number(editingItem.takenToProject) || 0);
    setReturnedFromProject(Number(editingItem.returnedFromProject) || 0);
    setDailyNotes(editingItem.dailyNotes || '');
    setFormError(null);
    setProjectError(null);
    setStockError(null);
  }, [editingItem]);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) loadEditForm();
    else resetAddForm();
  }, [isOpen, isEdit, loadEditForm, resetAddForm]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const fetchProjectContext = useCallback(async (code) => {
    const trimmed = String(code || '').trim();
    if (!trimmed) {
      setProjectName('');
      setBranchLocation('');
      setActivities([]);
      setActivity('');
      setDivision('');
      return;
    }

    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) {
      setProjectError('Backend URL not configured.');
      return;
    }

    setProjectLoading(true);
    setProjectError(null);
    try {
      const response = await axios.get(
        `${baseUrl}/api/miter/production-performance/inventory-ledger/project-context/${encodeURIComponent(trimmed)}`,
        await getAuthConfig()
      );
      if (response.data?.status === 'success' && response.data?.data) {
        const ctx = response.data.data;
        setProjectName(ctx.projectName || '');
        setBranchLocation(ctx.branchLocation || '');
        setActivities(Array.isArray(ctx.activities) ? ctx.activities : []);
        setActivity('');
        setDivision('');
        setSelectedStockItemIds([]);
        setItemEntryValues({});
        setIsInventoryPickerOpen(false);
        if (!ctx.activities?.length) {
          setProjectError('No activities found for this project. Upload production tracking or add inventory from the project dashboard first.');
        }
      } else {
        throw new Error(response.data?.message || 'Failed to load project details');
      }
    } catch (err) {
      setProjectName('');
      setBranchLocation('');
      setActivities([]);
      setActivity('');
      setDivision('');
      setProjectError(err.response?.data?.message || err.message || 'Failed to load project details');
    } finally {
      setProjectLoading(false);
    }
  }, [getAuthConfig]);

  const loadAllProjects = useCallback(async () => {
    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) return;

    const cached = localStorage.getItem('projectsList');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          setAllProjects(parsed.filter((project) => project?.Project__r != null));
        }
      } catch {
        // ignore invalid cache
      }
    }

    setProjectsLoading(true);
    try {
      const response = await axios.get(
        `${baseUrl}/api/salesforce/all-opportunities`,
        await getAuthConfig()
      );
      const records = Array.isArray(response.data?.records) ? response.data.records : [];
      const filtered = records.filter((project) => project?.Project__r != null);
      setAllProjects(filtered);
      localStorage.setItem('projectsList', JSON.stringify(filtered));
    } catch (err) {
      console.error('Failed to load projects for search:', err);
      if (!cached) {
        setProjectError('Failed to load projects. Please try again.');
      }
    } finally {
      setProjectsLoading(false);
    }
  }, [getAuthConfig]);

  useEffect(() => {
    if (!isOpen || isEdit) return;
    loadAllProjects();
  }, [isOpen, isEdit, loadAllProjects]);

  const filteredProjects = useMemo(() => {
    const query = projectSearchQuery.trim().toLowerCase();
    let list = allProjects.filter(
      (project) =>
        getProjectIdFromRecord(project) && project.dv_Division_New__c !== 'Material Sales'
    );

    if (query) {
      list = list.filter((project) => {
        const id = getProjectIdFromRecord(project).toLowerCase();
        const name = getProjectNameFromRecord(project).toLowerCase();
        return id.includes(query) || name.includes(query);
      });
    }

    return list.slice(0, PROJECT_SEARCH_RESULT_LIMIT);
  }, [allProjects, projectSearchQuery]);

  useEffect(() => {
    if (!isProjectDropdownOpen) return undefined;

    const handleClickOutside = (event) => {
      if (projectSearchRef.current && !projectSearchRef.current.contains(event.target)) {
        setIsProjectDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProjectDropdownOpen]);

  useEffect(() => {
    if (!isActivityDropdownOpen) return undefined;

    const handleClickOutside = (event) => {
      if (activityDropdownRef.current && !activityDropdownRef.current.contains(event.target)) {
        setIsActivityDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isActivityDropdownOpen]);

  const clearProjectSelection = useCallback(() => {
    setProjectNo('');
    setProjectName('');
    setBranchLocation('');
    setActivities([]);
    setActivity('');
    setDivision('');
    setSelectedStockItemIds([]);
    setItemEntryValues({});
    setIsInventoryPickerOpen(false);
    setProjectError(null);
  }, []);

  const handleSelectProject = useCallback(
    (project) => {
      const id = getProjectIdFromRecord(project);
      const name = getProjectNameFromRecord(project);
      if (!id) return;

      setProjectNo(id);
      setProjectName(name);
      setProjectSearchQuery(formatProjectOptionLabel(project));
      setIsProjectDropdownOpen(false);
      fetchProjectContext(id);
    },
    [fetchProjectContext]
  );

  const handleProjectSearchChange = (value) => {
    setProjectSearchQuery(value);
    setIsProjectDropdownOpen(true);

    if (!value.trim()) {
      clearProjectSelection();
      return;
    }

    const selectedLabel =
      projectNo && projectName ? `${projectNo} — ${projectName}` : projectNo;
    if (value.trim() !== selectedLabel.trim()) {
      setProjectNo('');
      setProjectName('');
      setBranchLocation('');
      setActivities([]);
      setActivity('');
      setDivision('');
      setSelectedStockItemIds([]);
      setItemEntryValues({});
      setIsInventoryPickerOpen(false);
      setProjectError(null);
    }
  };

  useEffect(() => {
    if (!isOpen || isEdit || !activity) {
      if (!isEdit) {
        setStockOptions([]);
        setSelectedStockItemIds([]);
        setItemEntryValues({});
        setIsInventoryPickerOpen(false);
      }
      return undefined;
    }

    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) return undefined;

    let cancelled = false;
    setStockLoading(true);
    setStockError(null);

    (async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/api/miter/production-performance/stock-inventory-items?activityClass=${encodeURIComponent(activity)}`,
          await getAuthConfig()
        );
        if (cancelled) return;
        if (response.data?.status === 'success' && Array.isArray(response.data?.data)) {
          setStockOptions(response.data.data);
        } else {
          setStockOptions([]);
        }
        setSelectedStockItemIds([]);
        setItemEntryValues({});
        setIsInventoryPickerOpen(false);
      } catch (err) {
        if (cancelled) return;
        setStockOptions([]);
        setStockError(err.response?.data?.message || err.message || 'Failed to load inventory items');
      } finally {
        if (!cancelled) setStockLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isEdit, activity, getAuthConfig]);

  useEffect(() => {
    if (!isOpen || isEdit || !projectNo.trim() || !activity || !workDateIso) {
      if (!isEdit) {
        setExistingStockItemIds([]);
      }
      return undefined;
    }

    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) return undefined;

    let cancelled = false;
    setExistingItemsLoading(true);

    (async () => {
      try {
        const params = new URLSearchParams({
          division: activity,
          workDate: workDateIso,
        });
        const response = await axios.get(
          `${baseUrl}/api/miter/production-performance/${encodeURIComponent(projectNo.trim())}/inventory-entry?${params}`,
          await getAuthConfig()
        );
        if (cancelled) return;

        const tableRows = response.data?.data?.tableRows;
        const ids = new Set();
        if (Array.isArray(tableRows)) {
          const dateRow = tableRows.find((row) => row.workDateIso === workDateIso);
          (dateRow?.items || []).forEach((item) => {
            const id = item.stockItemId ?? item.itemKey;
            if (id != null && id !== '') ids.add(String(id));
          });
        }
        setExistingStockItemIds(Array.from(ids));
        setSelectedStockItemIds((prev) => prev.filter((id) => !ids.has(String(id))));
        setItemEntryValues((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((key) => {
            if (ids.has(key)) delete next[key];
          });
          return next;
        });
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load existing inventory items for date:', err);
        setExistingStockItemIds([]);
      } finally {
        if (!cancelled) setExistingItemsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isEdit, projectNo, activity, workDateIso, getAuthConfig]);

  const handleActivityChange = (value) => {
    setActivity(value);
    const match = activities.find((item) => item.activity === value);
    setDivision(match?.division || '');
    setSelectedStockItemIds([]);
    setItemEntryValues({});
    setIsInventoryPickerOpen(false);
    setIsActivityDropdownOpen(false);
  };

  const handleWorkDateChange = (nextIso) => {
    setWorkDateIso(nextIso);
    setSelectedStockItemIds([]);
    setItemEntryValues({});
    setIsInventoryPickerOpen(false);
  };

  const toggleStockItemSelection = (itemId, checked) => {
    const id = String(itemId);
    setSelectedStockItemIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return Array.from(next);
    });
    setItemEntryValues((prev) => {
      const next = { ...prev };
      if (checked) {
        next[id] = next[id] || createDefaultItemEntry();
      } else {
        delete next[id];
      }
      return next;
    });
  };

  const updateItemEntryValue = (itemId, field, value) => {
    const id = String(itemId);
    setItemEntryValues((prev) => ({
      ...prev,
      [id]: {
        ...createDefaultItemEntry(),
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const getItemEntryValue = (itemId) => {
    const id = String(itemId);
    return {
      ...createDefaultItemEntry(),
      ...itemEntryValues[id],
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) {
      setFormError('Backend URL not configured.');
      return;
    }

    const { savedByName, savedByEmail } = getSavedByPayload(accounts);

    if (isEdit) {
      try {
        setSubmitting(true);
        const payload = {
          takenToProject: Number(takenToProject) || 0,
          returnedFromProject: Number(returnedFromProject) || 0,
          dailyNotes: dailyNotes || null,
          savedByName: savedByName || undefined,
          savedByEmail: savedByEmail || undefined,
        };

        const response = await axios.put(
          `${baseUrl}/api/miter/production-performance/inventory-ledger/${editingItem.id}`,
          payload,
          await getAuthConfig()
        );

        if (response.data?.status === 'success' || response.status === 200) {
          onSaved?.();
          onClose?.();
        } else {
          throw new Error(response.data?.message || 'Failed to update ledger entry');
        }
      } catch (err) {
        setFormError(err.response?.data?.message || err.message || 'Failed to update ledger entry');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!workDateIso) {
      setFormError('Date is required.');
      return;
    }
    if (!projectNo.trim()) {
      setFormError('Please search and select a project.');
      return;
    }
    if (!activity) {
      setFormError('Activity is required.');
      return;
    }
    if (!selectedStockItemIds.length) {
      setFormError('Please select at least one inventory item.');
      return;
    }

    try {
      setSubmitting(true);
      const failures = [];
      for (const stockItemId of selectedStockItemIds) {
        const entry = getItemEntryValue(stockItemId);
        try {
          const response = await axios.post(
            `${baseUrl}/api/miter/production-performance/inventory-ledger`,
            {
              date: isoToMDY(workDateIso),
              projectNo: projectNo.trim(),
              activity,
              stockItemId: Number(stockItemId),
              takenToProject: Number(entry.takenToProject) || 0,
              returnedFromProject: Number(entry.returnedFromProject) || 0,
              dailyNotes: entry.dailyNotes || null,
              savedByName: savedByName || undefined,
              savedByEmail: savedByEmail || undefined,
            },
            await getAuthConfig()
          );
          if (response.data?.status !== 'success') {
            failures.push(response.data?.message || `Failed for item ${stockItemId}`);
          }
        } catch (err) {
          failures.push(
            err.response?.data?.message || err.message || `Failed for item ${stockItemId}`
          );
        }
      }

      if (failures.length === 0) {
        onSaved?.();
        onClose?.();
      } else if (failures.length < selectedStockItemIds.length) {
        setFormError(`Some items were added, but others failed: ${failures.join('; ')}`);
        onSaved?.();
      } else {
        throw new Error(failures.join('; '));
      }
    } catch (err) {
      setFormError(err.response?.data?.message || err.message || 'Failed to create ledger entries');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const displayInventoryId = isEdit ? editingItem.inventoryId : null;
  const displayDescription = isEdit ? editingItem.description : null;
  const displayUom = isEdit ? editingItem.uom : null;
  const displayCostCode = isEdit ? editingItem.costCode : null;

  return createPortal(
    <div className="inventory-ledger-modal-overlay">
      <div className="inventory-ledger-modal-sheet">
        <div className="inventory-ledger-modal-header">
          <h2 className="inventory-ledger-modal-title">
            {isEdit ? 'Edit Ledger Entry' : 'Add New Inventory Transaction'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inventory-ledger-modal-close"
            aria-label="Close"
          >
            {LABEL_CLOSE_ICON}
          </button>
        </div>

        <div className="inventory-ledger-modal-body">
          <form
            id={INVENTORY_LEDGER_FORM_ID}
            onSubmit={handleSubmit}
            className="space-y-4"
          >
              {formError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {isEdit ? (
                <div>
                  <label className={labelClass}>Date</label>
                  <input type="text" readOnly value={isoToMDY(workDateIso)} className={readOnlyClass} />
                </div>
              ) : (
                <NativeDateField
                  label="Date"
                  labelClassName={labelClass}
                  inputClassName={inputClass}
                  placeholder="M/D/YYYY"
                  formatDisplay={isoToMDY}
                  value={workDateIso}
                  onChange={handleWorkDateChange}
                  required
                  className="md:col-span-1"
                />
              )}

            {isEdit ? (
              <ReadOnlyField label="Project ID" value={projectNo} />
            ) : (
              <div className="md:col-span-2" ref={projectSearchRef}>
                <label className={labelClass}>Search Project</label>
                <div className="relative">
                  <div className="inventory-ledger-project-search">
                    <FiSearch className="h-4 w-4 shrink-0 text-zinc-400" aria-hidden="true" />
                    <input
                      type="search"
                      placeholder="Search by project ID or name"
                      value={projectSearchQuery}
                      onChange={(e) => handleProjectSearchChange(e.target.value)}
                      onFocus={() => setIsProjectDropdownOpen(true)}
                      className="inventory-ledger-project-search__input w-full"
                      autoComplete="off"
                    />
                  </div>

                  {isProjectDropdownOpen && (
                    <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg inventory-ledger-project-dropdown">
                      {projectsLoading ? (
                        <div className="px-4 py-3 text-sm text-gray-500">Loading projects…</div>
                      ) : filteredProjects.length > 0 ? (
                        filteredProjects.map((project) => {
                          const id = getProjectIdFromRecord(project);
                          const name = getProjectNameFromRecord(project);
                          return (
                            <button
                              key={project.Id || id}
                              type="button"
                              onClick={() => handleSelectProject(project)}
                              className="w-full px-4 py-2.5 text-left text-sm hover:bg-red-50 border-b border-gray-100 last:border-b-0"
                            >
                              <span className="font-medium text-gray-900">{id}</span>
                              {name && (
                                <span className="text-gray-600"> — {name}</span>
                              )}
                            </button>
                          );
                        })
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-500">
                          {projectSearchQuery.trim()
                            ? 'No projects match your search.'
                            : 'Type a project ID or name to search.'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {projectNo && (
                  <p className="mt-1 text-xs text-gray-600">
                    Selected: <span className="font-medium">{projectNo}</span>
                    {projectName ? ` — ${projectName}` : ''}
                  </p>
                )}
                {projectLoading && (
                  <p className="mt-1 text-xs text-gray-500">Loading project details…</p>
                )}
                {projectError && (
                  <p className="mt-1 text-xs text-amber-700">{projectError}</p>
                )}
              </div>
            )}

            <ReadOnlyField label="Project Name" value={projectName} />
            <ReadOnlyField label="Branch (Location)" value={branchLocation} />

            {isEdit ? (
              <>
                <ReadOnlyField label="Activity" value={activity} />
                <ReadOnlyField label="Division" value={division} />
              </>
            ) : (
              <div className="md:col-span-2" ref={activityDropdownRef}>
                <label className={labelClass}>Activity</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      if (!activities.length || projectLoading) return;
                      setIsInventoryPickerOpen(false);
                      setIsActivityDropdownOpen((open) => !open);
                    }}
                    disabled={!activities.length || projectLoading}
                    className={`${inputClass} h-10 text-left flex items-center justify-between gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
                    aria-haspopup="listbox"
                    aria-expanded={isActivityDropdownOpen}
                  >
                    <span className={activity ? 'text-gray-900' : 'text-gray-500'}>
                      {activity || 'Select activity…'}
                    </span>
                    <svg
                      className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${isActivityDropdownOpen ? 'rotate-180' : ''}`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {isActivityDropdownOpen && (
                    <div
                      className="absolute left-0 right-0 top-full z-30 mt-1 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg inventory-ledger-activity-dropdown"
                      role="listbox"
                    >
                      {activities.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500">No activities available.</div>
                      ) : (
                        activities.map((item) => {
                          const isSelected = activity === item.activity;
                          return (
                            <button
                              key={item.activity}
                              type="button"
                              role="option"
                              aria-selected={isSelected}
                              onClick={() => handleActivityChange(item.activity)}
                              className={`w-full px-4 py-2.5 text-left text-sm border-b border-gray-100 last:border-b-0 ${
                                isSelected ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-900 hover:bg-gray-50'
                              }`}
                            >
                              {item.activity}
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
                {activity && <p className="mt-1 text-xs text-gray-500">Division: {division || '—'}</p>}
              </div>
            )}

            {isEdit ? (
              <>
                <ReadOnlyField label="Inventory ID" value={displayInventoryId} />
                <ReadOnlyField label="Description" value={displayDescription} className="md:col-span-2" />
                <ReadOnlyField label="UOM" value={displayUom} />
                <ReadOnlyField label="Cost Code" value={displayCostCode} />
              </>
            ) : (
              <div className="md:col-span-2">
                <label className={labelClass}>Inventory Items</label>
                <button
                  type="button"
                  onClick={() => {
                    setIsActivityDropdownOpen(false);
                    setIsInventoryPickerOpen((open) => !open);
                  }}
                  disabled={!activity || stockLoading || existingItemsLoading}
                  className="w-full h-10 px-4 rounded-lg border border-gray-300 bg-white text-sm text-left text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {stockLoading || existingItemsLoading
                    ? 'Loading inventory items…'
                    : selectedStockItemIds.length > 0
                      ? `${selectedStockItemIds.length} item(s) selected`
                      : 'Select inventory items…'}
                </button>

                {isInventoryPickerOpen && (
                  <div className="mt-2 rounded-lg border border-gray-200 bg-white shadow-sm">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                      <h3 className="text-sm font-semibold text-gray-900">Add Inventory Item</h3>
                      <button
                        type="button"
                        onClick={() => setIsInventoryPickerOpen(false)}
                        className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                        aria-label="Close"
                      >
                        &times;
                      </button>
                    </div>
                    <div className="px-4 py-3 max-h-[min(50vh,28rem)] overflow-y-auto inventory-ledger-inventory-picker-list">
                        {stockLoading || existingItemsLoading ? (
                          <p className="text-sm text-gray-500">Loading inventory items…</p>
                        ) : stockError ? (
                          <p className="text-sm text-red-600">{stockError}</p>
                        ) : stockOptions.length === 0 ? (
                          <p className="text-sm text-gray-500">
                            No inventory items found for this activity. Upload stock inventory items from Configuration Engine.
                          </p>
                        ) : availableStockOptions.length === 0 ? (
                          <p className="text-sm text-gray-500">
                            All inventory items are already added for this project, activity, and date.
                          </p>
                        ) : (
                          <>
                            <p className="text-xs text-gray-500 mb-3">Select inventory items to add:</p>
                            <div className="space-y-2">
                              {availableStockOptions.map((item) => {
                                const itemId = String(item.id);
                                return (
                                  <label
                                    key={itemId}
                                    className="flex gap-2 items-start cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedStockItemIds.includes(itemId)}
                                      onChange={(e) => toggleStockItemSelection(itemId, e.target.checked)}
                                      className="mt-0.5 rounded border-gray-300"
                                    />
                                    <span className="text-sm text-gray-800">
                                      <span className="font-medium">{item.inventory_id}</span>
                                      {' — '}
                                      {item.description}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          </>
                        )}
                    </div>
                    <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                      <button
                        type="button"
                        onClick={() => setIsInventoryPickerOpen(false)}
                        className="w-full h-9 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700"
                      >
                        Done ({selectedStockItemIds.length} selected)
                      </button>
                    </div>
                  </div>
                )}

                {stockError && !isInventoryPickerOpen && (
                  <p className="mt-1 text-xs text-amber-700">{stockError}</p>
                )}

                {selectedStockItems.length > 0 && (
                  <div className="mt-4 space-y-4">
                    {selectedStockItems.map((item) => {
                      const entry = getItemEntryValue(item.id);
                      return (
                        <div
                          key={item.id}
                          className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                        >
                          <div className="mb-3">
                            <p className="text-sm font-semibold text-gray-900">
                              {item.inventory_id} — {item.description}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              UOM: {item.uom}
                              {item.cost_code ? ` · Cost Code: ${item.cost_code}` : ''}
                            </p>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className={labelClass}>Taken to Project</label>
                              <input
                                type="number"
                                min="0"
                                step="any"
                                value={entry.takenToProject}
                                onChange={(e) =>
                                  updateItemEntryValue(item.id, 'takenToProject', e.target.value)
                                }
                                className={inputClass}
                              />
                            </div>
                            <div>
                              <label className={labelClass}>Returned from Project</label>
                              <input
                                type="number"
                                min="0"
                                step="any"
                                value={entry.returnedFromProject}
                                onChange={(e) =>
                                  updateItemEntryValue(item.id, 'returnedFromProject', e.target.value)
                                }
                                className={inputClass}
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className={labelClass}>Daily Notes</label>
                              <textarea
                                rows={2}
                                value={entry.dailyNotes}
                                onChange={(e) =>
                                  updateItemEntryValue(item.id, 'dailyNotes', e.target.value)
                                }
                                placeholder="Optional notes"
                                className={inputClass}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {isEdit && (
              <>
            <div>
              <label className={labelClass}>Taken to Project</label>
              <input
                type="number"
                min="0"
                step="any"
                required
                value={takenToProject}
                onChange={(e) => setTakenToProject(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Returned from Project</label>
              <input
                type="number"
                min="0"
                step="any"
                required
                value={returnedFromProject}
                onChange={(e) => setReturnedFromProject(e.target.value)}
                className={inputClass}
              />
            </div>

            <div className="md:col-span-2">
              <label className={labelClass}>Daily Notes</label>
              <textarea
                rows={2}
                value={dailyNotes}
                onChange={(e) => setDailyNotes(e.target.value)}
                placeholder="Optional notes"
                className={inputClass}
              />
            </div>
              </>
            )}
              </div>

          </form>
        </div>

        <div className="inventory-ledger-modal-footer">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inventory-ledger-modal-btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            form={INVENTORY_LEDGER_FORM_ID}
            disabled={submitting || (!isEdit && (!projectNo || !activity || selectedStockItemIds.length === 0))}
            className="inventory-ledger-modal-btn-primary"
          >
            {submitting
              ? 'Saving…'
              : isEdit
                ? 'Save Entry'
                : selectedStockItemIds.length > 1
                  ? `Add ${selectedStockItemIds.length} Transactions`
                  : 'Add Transaction'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default InventoryLedgerModal;
